import React, { useEffect, useState } from 'react';
import { Calendar, MapPin, Search, Loader2, ShieldCheck, FileDown } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};
const INVOICE_STATES = new Set(['Completed','completed','Paid','paid','Finished','finished']);
const downloadInvoice = async (bookingId) => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  try {
    const r = await fetch(`${API}/bookings/${bookingId}/invoice.pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!r.ok) { alert(`Could not fetch invoice (HTTP ${r.status})`); return; }
    const invoiceNo = r.headers.get('x-invoice-number') || bookingId;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${invoiceNo}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  } catch (e) { alert('Download failed: ' + (e?.message || e)); }
};

const STATUS_STYLE = {
  Pending:  'bg-[#E8F5E9] text-[#2E7D32]',
  Accepted: 'bg-[#FEF3C7] text-[#D97706]',
  EnRoute:  'bg-[#DBEAFE] text-[#1D4ED8]',
  Started:  'bg-[#EDE9FE] text-[#6D28D9]',
  Finished: 'bg-neutral-100 text-neutral-600',
};

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');

  useEffect(() => {
    axios.get(`${API}/admin/bookings`, authCfg())
      .then(({ data }) => setBookings(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = bookings.filter((b) => {
    if (status !== 'all' && b.status !== status) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      b.id.toLowerCase().includes(s) ||
      (b.user_name || '').toLowerCase().includes(s) ||
      (b.user_email || '').toLowerCase().includes(s) ||
      (b.cleaner_name || '').toLowerCase().includes(s)
    );
  });

  return (
    <AdminLayout title="Bookings (Web + Mobile)">
      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row gap-3 mb-5">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-bookings-search" placeholder="Search by ID, customer, cleaner..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:border-[#2E7D32]" />
            </div>
            <select data-testid="admin-bookings-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
              <option value="all">All statuses</option>
              <option value="Pending">Pending</option>
              <option value="Accepted">Accepted</option>
              <option value="EnRoute">En Route</option>
              <option value="Started">Started</option>
              <option value="Finished">Finished</option>
            </select>
            <p className="text-sm text-neutral-500 md:ml-auto md:self-center" data-testid="admin-bookings-total">Total: {filtered.length}</p>
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid="admin-bookings-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <Th>Booking</Th><Th>Customer</Th><Th>Cleaner</Th><Th>Schedule</Th><Th>Amount</Th><Th>Status</Th><Th>Invoice</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr key={b.id} data-testid={`admin-booking-${b.id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-4 py-3">
                        <p className="font-mono text-xs text-neutral-500">{b.id}</p>
                        <p className="font-semibold">{b.items?.[0]?.name}{b.items?.length > 1 ? ` +${b.items.length - 1}` : ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold">{b.user_name || 'Guest'}</p>
                        <p className="text-xs text-neutral-500">{b.user_email || '-'}</p>
                      </td>
                      <td className="px-4 py-3">
                        {b.cleaner_name ? (
                          <p className="text-xs text-neutral-700">{b.cleaner_name}</p>
                        ) : <span className="text-xs text-neutral-400">Unassigned</span>}
                      </td>
                      <td className="px-4 py-3">
                        <p className="flex items-center gap-1 text-xs"><Calendar className="w-3 h-3" /> {b.date}</p>
                        <p className="text-xs text-neutral-500">{b.slot}</p>
                        <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {b.address}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-bold text-[#EA580C]">₹{b.total}</p>
                        <p className="text-xs text-neutral-500 capitalize">{b.payment}{b.wallet_paid ? ` + ₹${b.wallet_paid} wallet` : ''}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${STATUS_STYLE[b.status] || 'bg-neutral-100 text-neutral-500'}`}>{b.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        {INVOICE_STATES.has(b.status) ? (
                          <button
                            data-testid={`admin-booking-invoice-${b.id}`}
                            onClick={() => downloadInvoice(b.id)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E7D32] hover:text-[#1B5E20]">
                            <FileDown className="w-3.5 h-3.5" /> PDF
                          </button>
                        ) : (
                          <span className="text-[11px] text-neutral-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-16 text-center text-neutral-500">No bookings found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-neutral-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#2E7D32]" /> Showing every booking from the shared bookings collection (web + mobile combined).</p>
        </>
      )}
    </AdminLayout>
  );
};

const Th = ({ children }) => <th className="text-left px-4 py-2.5 text-xs font-bold text-neutral-500 uppercase tracking-wider">{children}</th>;

export default AdminBookings;
